import type {
  SubscriptionBillingCadence,
  WorkspaceBillingActivityPageContract,
  WorkspaceBillingActivityType,
  WorkspaceBillingContract,
  WorkspaceWalletContract,
} from '@nest-react/domain';

type ApiRequest = <T>(path: string, options?: RequestInit) => Promise<T>;

export interface YooKassaCheckoutContract {
  action: 'REDIRECT' | 'RESOLVE_PENDING';
  orderId: string;
  paymentId: string;
  status: string;
  confirmationUrl: string | null;
  pendingOrder?: {
    plan: 'STANDARD' | 'PRO' | 'MAX';
    billingCadence: SubscriptionBillingCadence;
    amount: string;
    currency: string;
    createdAt: string;
  };
}

export const createBillingRepository = (request: ApiRequest) => ({
  getWallet: (workspaceId: string): Promise<WorkspaceWalletContract> =>
    request<WorkspaceWalletContract>(`/workspaces/${workspaceId}/wallet`),
  getSummary: (workspaceId: string): Promise<WorkspaceBillingContract> =>
    request<WorkspaceBillingContract>(`/workspaces/${workspaceId}/billing`),
  createYooKassaCheckout: (
    workspaceId: string,
    plan: 'STANDARD' | 'PRO' | 'MAX',
    billingCadence: SubscriptionBillingCadence,
    idempotencyKey: string
  ): Promise<YooKassaCheckoutContract> =>
    request<YooKassaCheckoutContract>(
      `/workspaces/${workspaceId}/billing/yookassa/checkout`,
      {
        method: 'POST',
        body: JSON.stringify({ plan, billingCadence, idempotencyKey }),
      }
    ),
  createYooKassaCreditCheckout: (
    workspaceId: string,
    idempotencyKey: string
  ): Promise<YooKassaCheckoutContract> =>
    request<YooKassaCheckoutContract>(
      `/workspaces/${workspaceId}/billing/yookassa/credits/checkout`,
      {
        method: 'POST',
        body: JSON.stringify({ idempotencyKey }),
      }
    ),
  abandonYooKassaOrder: (
    workspaceId: string,
    orderId: string
  ): Promise<{ status: string }> =>
    request<{ status: string }>(
      `/workspaces/${workspaceId}/billing/yookassa/orders/${orderId}/abandon`,
      { method: 'POST' }
    ),
  getActivity: (
    workspaceId: string,
    options: {
      type: WorkspaceBillingActivityType | null;
      cursor: string | null;
    }
  ): Promise<WorkspaceBillingActivityPageContract> => {
    const params = new URLSearchParams();
    if (options.type) params.set('type', options.type);
    if (options.cursor) params.set('cursor', options.cursor);
    const query = params.toString();
    return request<WorkspaceBillingActivityPageContract>(
      `/workspaces/${workspaceId}/billing/activity${query ? `?${query}` : ''}`
    );
  },
});
